/**
 * What the uploaded prior art turned out to be, for the filer to confirm.
 *
 * Deliberately a confirmation step rather than a direct fill. These numbers go onto a
 * paper filed at the USPTO, and a digit read off a bad scan cites a document that does
 * not exist — which is exactly the mistake the examiner sends back. The filer has the
 * documents open in front of them and is the only one who can say the number is right,
 * so nothing reaches the form until they have looked at each row.
 *
 * Every row is editable, including the kind: the extraction reports a publication as a
 * publication and a patent as a patent, but a cover sheet bound in front of a document
 * can make it read as the wrong one, and retyping the number in the right box is worse
 * than moving it.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, FileText, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ExtractedArt, ExtractedArtDocument, ExtractedKind } from '@/types/ids';

/** What the filer can put a row into. `not_found` is not offered as a choice: it is a
 *  result, not a citation. A row that came back without a number arrives here as NPL,
 *  which is what a document with no number of its own is cited as. */
const KIND_OPTIONS: Array<{ value: ExtractedKind; label: string }> = [
  { value: 'us_patent', label: 'US patent' },
  { value: 'us_publication', label: 'US publication' },
  { value: 'foreign_patent', label: 'Foreign patent' },
  { value: 'npl', label: 'Non-patent literature' },
];

/** Which of the form's three boxes each kind is written into. */
const FIELD_OF_KIND: Record<string, 'referencesText' | 'foreignReferencesText' | 'nplText'> = {
  us_patent: 'referencesText',
  us_publication: 'referencesText',
  foreign_patent: 'foreignReferencesText',
  npl: 'nplText',
};


const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** "18087653" -> "18/087,653". Left alone if it is not eight digits. */
function formatSerial(digits: string): string {
  return digits.length === 8
    ? `${digits.slice(0, 2)}/${digits.slice(2, 5)},${digits.slice(5)}`
    : digits;
}

/** A date segment as a citation spells it: "07-15-2026" -> "July 15, 2026". */
function formatFilenameDate(segment: string): string {
  const parts = segment.split(/[-/.]/).map(p => p.trim()).filter(Boolean);
  if (parts.length !== 3) return '';
  let year: string, month: string, day: string;
  if (parts[0].length === 4) {
    [year, month, day] = parts;              // 2026-07-15
  } else {
    [month, day, year] = parts;              // 07-15-2026
  }
  const m = Number(month);
  const d = Number(day);
  const y = Number(year);
  if (!m || !d || !y || m < 1 || m > 12 || d < 1 || d > 31 || year.length !== 4) return '';
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

/**
 * A citation proposed from the filename, for a document the reader could put no number to.
 *
 * Firms file prosecution documents under names that already carry the citation:
 * "18087653 - 07-15-2026 - Non-Final Rejection.pdf" gives the application, the date and
 * what the document is. Offering that as an editable starting point is better than an
 * empty box — the filer corrects a sentence instead of composing one — and it uses only
 * what they named the file, so nothing is invented.
 *
 * Returns "" where the filename says nothing useful, which leaves the box empty rather
 * than proposing the filename as a citation.
 */
export function proposeNplCitation(filename: string): string {
  const stem = String(filename || '').replace(/\.[A-Za-z0-9]+$/, '').trim();
  if (!stem) return '';

  const segments = stem
    .split(/\s+[-–—]\s+|_{1,2}/)
    .map(part => part.trim())
    .filter(Boolean);

  let serial = '';
  let date = '';
  const rest: string[] = [];

  segments.forEach(segment => {
    const digits = segment.replace(/[^0-9]/g, '');
    const asDate = formatFilenameDate(segment);
    if (!date && asDate) {
      date = asDate;
    } else if (!serial && /^[\d/,]+$/.test(segment) && (digits.length === 7 || digits.length === 8)) {
      serial = formatSerial(digits);
    } else {
      rest.push(segment);
    }
  });

  // What the document is. Without it there is no citation to propose: a date and a serial
  // number on their own do not say what is being cited.
  const description = rest.join(' ').replace(/\s{2,}/g, ' ').trim();
  if (!description) return '';

  const pieces = [description];
  if (date) pieces.push(`dated ${date}`);
  if (serial) pieces.push(`in U.S. Appl. No. ${serial}`);
  // A description alone is the filename restated, which is not a citation.
  return pieces.length > 1 ? pieces.join(' ') : '';
}

export interface ConfirmedArt {
  referencesText: string[];
  foreignReferencesText: string[];
  nplText: string[];
}

/** One row as the filer has it, which is the extraction's reading until they change it. */
interface Row {
  id: string;
  name: string;
  kind: ExtractedKind;
  value: string;
  page: number;
  status: ExtractedArtDocument['status'];
  /** The citation was proposed from the filename rather than read off the page. Worth
   *  saying on the row: it is the one value here that nothing actually verified. */
  proposed: boolean;
  include: boolean;
}

function toRows(extracted: ExtractedArt | null): Row[] {
  return (extracted?.documents || []).map(document => {
    const proposedValue = document.value ? '' : proposeNplCitation(document.name);
    return {
    id: document.id,
    name: document.name,
    // `not_found` is carried into the row as NPL, because that is what the row offers and
    // what it is written into. Left as `not_found`, the kind select would SHOW "Non-patent
    // literature" while the row still held `not_found` — and `FIELD_OF_KIND` has no entry
    // for that, so a row the filer ticked and typed a citation into was silently dropped
    // on confirm. `status` below still records that nothing was read, which is what the
    // warning on the row and the default-off tick are driven from.
    kind: document.kind === 'not_found' ? 'npl' : document.kind,
    // A row with no citation gets one proposed from its filename. The worker proposes one
    // from the page where it can; this covers the case where it could not, so the filer
    // edits a sentence rather than composing one into an empty box.
    value: document.value || proposedValue,
    page: document.page,
    status: document.status,
    proposed: Boolean(proposedValue),
    // Included where there is a citation to include - read off the page, or proposed from
    // the filename. A row with neither stays off: there is nothing in it to cite, and a
    // ticked empty row is a blank line on the form.
    include: document.status === 'found' || Boolean(proposedValue),
    };
  });
}

export function IdsPriorArtDialog({
  open,
  onOpenChange,
  extracted,
  loading,
  error,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  extracted: ExtractedArt | null;
  /** Still reading. The dialog opens on upload and fills in when the job finishes, so
   *  the filer can see what is happening rather than watching a button spin. */
  loading: boolean;
  error?: string;
  onConfirm: (confirmed: ConfirmedArt) => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);

  // Re-seeded whenever a new reading arrives. Keyed on the document list rather than on
  // `open` so that a dialog left open while the job finishes picks the results up.
  useEffect(() => {
    setRows(toRows(extracted));
  }, [extracted]);

  const update = (id: string, change: Partial<Row>) =>
    setRows(current => current.map(row => (row.id === id ? { ...row, ...change } : row)));

  const chosen = rows.filter(row => row.include && row.value.trim());
  // Only the rows with nothing in them. A row whose citation was proposed from its
  // filename was not read off the page, but it is not "could not be read" either - it has
  // a citation to check, and counting it as a failure tells the filer to go and fix
  // something that is already filled in.
  const unread = rows.filter(row => row.status !== 'found' && !row.value.trim());

  const grouped = useMemo(() => {
    const result: ConfirmedArt = {
      referencesText: [],
      foreignReferencesText: [],
      nplText: [],
    };
    chosen.forEach(row => {
      const field = FIELD_OF_KIND[row.kind];
      if (field) result[field].push(row.value.trim());
    });
    return result;
  }, [chosen]);

  const confirm = () => {
    onConfirm(grouped);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Confirm what was read from the documents</DialogTitle>
          <DialogDescription>
            These go on the form as filed, so check each number against the document it
            came from. Edit anything that was read wrongly, and untick anything you do not
            want cited.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 py-10 justify-center text-sm text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Reading the documents. Scanned ones are slower, as they have to be OCR'd.
          </div>
        )}

        {!loading && error && (
          <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="py-10 text-center text-sm text-slate-600">
            Nothing was read from those documents.
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {rows.map(row => (
              <div
                key={row.id}
                className={`rounded border p-3 ${
                  row.status === 'found'
                    ? 'border-slate-200'
                    : 'border-amber-200 bg-amber-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    className="mt-1"
                    checked={row.include}
                    onCheckedChange={checked =>
                      update(row.id, { include: Boolean(checked) })
                    }
                    aria-label={`Include ${row.name}`}
                  />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <FileText className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate font-medium">{row.name}</span>
                      {row.status === 'found' && row.page > 0 && (
                        <span className="shrink-0 text-slate-400">
                          found on page {row.page}
                        </span>
                      )}
                      {row.status === 'not_found' && row.proposed && (
                        <span className="shrink-0 text-amber-700">
                          no number on the document — cited as NPL from the filename,
                          check it
                        </span>
                      )}
                      {row.status === 'not_found' && !row.proposed && (
                        <span className="shrink-0 text-amber-700">
                          no number found — cite it as NPL, or type a number in
                        </span>
                      )}
                      {row.status === 'unreadable' && (
                        <span className="shrink-0 text-amber-700">
                          could not be opened
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row">
                      <div className="sm:w-48">
                        <Label className="sr-only">Kind</Label>
                        <Select
                          value={row.kind}
                          onValueChange={value =>
                            update(row.id, { kind: value as ExtractedKind })
                          }
                        >
                          <SelectTrigger className="h-9 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {KIND_OPTIONS.map(option => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Input
                        className="h-9 flex-1 text-sm"
                        value={row.value}
                        placeholder={
                          row.kind === 'npl'
                            ? 'Authors, "title", publication, pages, date'
                            : 'Document number'
                        }
                        onChange={event =>
                          update(row.id, { value: event.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="shrink-0 border-t pt-3 text-xs text-slate-600">
            {chosen.length} of {rows.length} will be added
            {unread.length > 0 && ` — ${unread.length} could not be read`}.
            {rows.some(row => row.proposed) &&
              ' Citations shown in amber were taken from the filename, not read off the' +
                ' page — check those against the document.'}
            {' '}
            US patents and publications go to the US box, foreign documents to the foreign
            box, and anything else to NPL. Nothing is overwritten: these are added to
            whatever is already typed in.
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={confirm} disabled={loading || chosen.length === 0}>
            <Check className="mr-2 h-4 w-4" />
            Add {chosen.length > 0 ? `${chosen.length} ` : ''}to the form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default IdsPriorArtDialog;
